import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Attractions, Close } from '@mui/icons-material';
import type {
  RuteroOfferOptionsSelection,
  RuteroTicketQuantity,
  RuteroTourDuration,
  RuteroTourSession,
} from '@bandido/shared/types';

import { TourOffer, TourProduct, tourPricePresentation } from '../tours';
import { formatMinutes } from '../format';
import { useAudience } from '../audience';
import { useOfferOptions, useTourOfferDetail } from '../queries';
import type { OfferOptions } from '../queries';
import { offerOptionsSelectionPayload } from '../offerOptionsSelection';
import { formatMoney } from '../money';
import {
  changeTourTicketQuantity,
  tourOptionsAwaitingPickup,
  tourOptionsDiscoveryRequest,
  tourOptionsResponseIsCurrent,
  tourPickupSelectionValid,
  tourSessionLabel,
  tourTicketQuantitiesValid,
} from '../tourSessions';
import { PriceBlock } from './PriceBlock';
import { MetaLine } from './MetaLine';
import { TourTicketQuantities } from './TourTicketQuantities';

interface Props {
  product?: TourProduct;
  partySize?: number;
  disabled?: boolean;
  disabledReason?: string;
  onContinue: (offer: TourOffer, selection: RuteroOfferOptionsSelection) => void;
  onClose: () => void;
}

function DetailList({ items }: { items: readonly string[] }) {
  return (
    <Stack component="ul" spacing={0.5} sx={{ pl: 2.25, my: 0 }}>
      {items.map((item, index) => (
        <Typography
          component="li"
          variant="body2"
          color="text.secondary"
          key={`${item}-${index}`}
          sx={{ whiteSpace: 'pre-line' }}
        >
          {item}
        </Typography>
      ))}
    </Stack>
  );
}

function durationLabel(duration: RuteroTourDuration | undefined): string | undefined {
  if (!duration) return undefined;
  return [formatMinutes(duration.minutes), duration.days ? `${duration.days} d` : undefined]
    .filter(Boolean)
    .join(' · ');
}

function statusLabel(session: RuteroTourSession, t: (key: string) => string): string[] {
  return [
    session.availabilityStatus ? t(`travelServices.tours.availabilityStatus.${session.availabilityStatus}`) : undefined,
    session.pricingStatus ? t(`travelServices.tours.pricingStatus.${session.pricingStatus}`) : undefined,
    ...(session.quoteRequirements ?? []).map((requirement) =>
      t(`travelServices.tours.quoteRequirements.${requirement}`),),
  ].filter((item): item is string => Boolean(item));
}

/**
 * Ficha única de Tours: contenido comercial y discovery de options viven en
 * la misma superficie. No pide PII ni elige pickup/sesión por el asesor.
 */
export function TourProductDialog({ product, partySize, disabled, disabledReason, onContinue, onClose }: Props) {
  const { t } = useTranslation();
  const pricePresentation = product ? tourPricePresentation(product.offer) : null;
  const { isAgent } = useAudience();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const detail = useTourOfferDetail();
  const offerOptions = useOfferOptions('tours');
  const provenance = product?.offer.provenance;
  const [options, setOptions] = useState<OfferOptions>();
  const [pickupId, setPickupId] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [ticketQuantities, setTicketQuantities] = useState<RuteroTicketQuantity[]>([]);
  const [activeImage, setActiveImage] = useState(0);
  const optionsRequest = useRef(0);

  const requestOptions = (selection: RuteroOfferOptionsSelection = {}) => {
    if (!provenance) return;
    const requestId = ++optionsRequest.current;
    const request = tourOptionsDiscoveryRequest(
      provenance,
      partySize,
      selection,
    );
    offerOptions.mutate(request, {
      onSuccess: (next) => {
        if (!tourOptionsResponseIsCurrent(requestId, optionsRequest.current)) return;
        setOptions(next);
        setSessionId((current) => (current && next.sessions?.some((session) => session.id === current) ? current : ''));
      },
      onError: () => {
        if (tourOptionsResponseIsCurrent(requestId, optionsRequest.current)) {
          setOptions((current) => current);
        }
      },
    });
  };

  useEffect(() => {
    optionsRequest.current += 1;
    detail.reset();
    offerOptions.reset();
    setOptions(undefined);
    setPickupId('');
    setSessionId('');
    setTicketQuantities([]);
    setActiveImage(0);
    if (provenance) {
      detail.mutate({ provenanceToken: provenance });
      requestOptions();
    }
  }, [provenance, partySize]);

  const currentDetail = provenance && detail.variables?.provenanceToken === provenance ? detail.data : undefined;
  const images = currentDetail?.images?.length
    ? currentDetail.images
    : product?.images?.length
      ? product.images.map((url, index) => ({
        url,
        alt: index === 0 ? product.title : undefined,
      }))
      : product?.image
        ? [{ url: product.image, alt: product.title }]
        : [];
  const duration = currentDetail?.facets?.duration
    ? durationLabel(currentDetail.facets.duration)
    : product?.durationDays
      ? t('travelServices.durationDays', { count: product.durationDays })
      : formatMinutes(product?.durationMinutes);
  const selectedSession = options?.sessions?.find((session) => session.id === sessionId);
  const selection = useMemo(
    () =>
      offerOptionsSelectionPayload({
        pickupId,
        sessionId,
        ticketQuantities,
      }),
    [pickupId, sessionId, ticketQuantities],
  );
  const pickupValid = tourPickupSelectionValid(options?.pickups, pickupId);
  const ticketQuantitiesValid = tourTicketQuantitiesValid(selectedSession, ticketQuantities);
  const canContinue = Boolean(
    product &&
      pickupValid &&
      selectedSession &&
      selectedSession.availability !== 0 &&
      ticketQuantitiesValid &&
      !disabled,
  );

  return (
    <Dialog open={Boolean(product)} onClose={onClose} maxWidth="lg" fullWidth fullScreen={fullScreen}>
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="flex-start" gap={1}>
          <Box flex={1} minWidth={0}>
            {isAgent && product?.provider && (
              <Typography component="p" color="text.disabled" sx={{ fontSize: 11.5 }}>
                {product.provider}
              </Typography>
            )}
            <Typography sx={{ fontSize: 18, fontWeight: 600, lineHeight: 1.3 }}>
              {currentDetail?.title ?? product?.title ?? t('travelServices.tours.productDetail')}
            </Typography>
            {product && (
              <MetaLine
                items={[
                  currentDetail?.facets?.category?.name ?? product.category,
                  duration,
                  currentDetail?.facets?.languages?.length
                    ? currentDetail.facets.languages.map((language) => language.name).join(' · ')
                    : product.languages?.length
                      ? product.languages.join(' · ')
                      : undefined,
                ]}
              />
            )}
          </Box>
          <Button size="small" onClick={onClose} startIcon={<Close />}>
            {t('travelServices.booking.close')}
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {product && (
          <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: 'minmax(0, 1fr) 340px' }} gap={2.25}>
            <Box minWidth={0}>
              {images.length > 0 ? (
                <Box mb={2.25}>
                  <Box
                    component="img"
                    src={images[Math.min(activeImage, images.length - 1)]?.url}
                    alt={images[Math.min(activeImage, images.length - 1)]?.alt ?? product.title}
                    sx={{
                      width: '100%',
                      height: { xs: 220, sm: 340 },
                      objectFit: 'cover',
                      borderRadius: 1,
                    }}
                  />
                  {images.length > 1 && (
                    <Box display="flex" gap={1} mt={1} overflow="auto" pb={0.5}>
                      {images.map((image, index) => (
                        <Box
                          key={`${image.url}-${index}`}
                          component="button"
                          type="button"
                          aria-label={image.alt ?? `${product.title} ${index + 1}`}
                          onClick={() => setActiveImage(index)}
                          sx={{
                            p: 0,
                            border: 2,
                            borderColor: activeImage === index ? 'primary.main' : 'transparent',
                            borderRadius: 1,
                            bgcolor: 'transparent',
                            cursor: 'pointer',
                            flex: '0 0 auto',
                          }}
                        >
                          <Box
                            component="img"
                            src={image.url}
                            alt=""
                            sx={{
                              display: 'block',
                              width: 76,
                              height: 54,
                              objectFit: 'cover',
                              borderRadius: 0.5,
                            }}
                          />
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              ) : (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  minHeight={180}
                  mb={2}
                  borderRadius={1}
                  bgcolor="grey.100"
                >
                  <Attractions color="action" sx={{ fontSize: 52 }} />
                </Box>
              )}

              {detail.isLoading && (
                <Box display="flex" justifyContent="center" py={3}>
                  <CircularProgress size={28} />
                </Box>
              )}
              {detail.isError && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {t('travelServices.tours.detailUnavailable')}
                </Alert>
              )}

              {currentDetail?.attributes && (
                <Box display="flex" flexWrap="wrap" gap={0.75} mb={2}>
                  {typeof currentDetail.attributes.freeCancellation === 'boolean' && (
                    <Chip
                      size="small"
                      label={t(
                        currentDetail.attributes.freeCancellation
                          ? 'travelServices.tours.freeCancellation'
                          : 'travelServices.tours.noFreeCancellation',
                      )}
                    />
                  )}
                  {typeof currentDetail.attributes.instantConfirmation === 'boolean' && (
                    <Chip
                      size="small"
                      label={t(
                        currentDetail.attributes.instantConfirmation
                          ? 'travelServices.tours.instantConfirmation'
                          : 'travelServices.tours.noInstantConfirmation',
                      )}
                    />
                  )}
                  {typeof currentDetail.attributes.mobileVoucher === 'boolean' && (
                    <Chip
                      size="small"
                      label={t(
                        currentDetail.attributes.mobileVoucher
                          ? 'travelServices.tours.mobileVoucher'
                          : 'travelServices.tours.noMobileVoucher',
                      )}
                    />
                  )}
                </Box>
              )}

              {(currentDetail?.description ?? product.description) && (
                <Box mb={2}>
                  <Typography variant="subtitle2" gutterBottom>
                    {t('travelServices.tours.description')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                    {currentDetail?.description ?? product.description}
                  </Typography>
                </Box>
              )}

              {currentDetail?.highlights?.length ? (
                <Box mb={2}>
                  <Typography variant="subtitle2" gutterBottom>
                    {t('travelServices.tours.highlights')}
                  </Typography>
                  <DetailList items={currentDetail.highlights} />
                </Box>
              ) : null}

              {currentDetail?.inclusions?.length ? (
                <Box mb={2}>
                  <Typography variant="subtitle2" gutterBottom>
                    {t('travelServices.tours.included')}
                  </Typography>
                  <DetailList items={currentDetail.inclusions} />
                </Box>
              ) : null}

              {currentDetail?.exclusions?.length ? (
                <Box mb={2}>
                  <Typography variant="subtitle2" gutterBottom>
                    {t('travelServices.tours.notIncluded')}
                  </Typography>
                  <DetailList items={currentDetail.exclusions} />
                </Box>
              ) : null}

              {currentDetail?.routes?.length ? (
                <Box mb={2}>
                  <Typography variant="subtitle2" gutterBottom>
                    {t('travelServices.tours.routes')}
                  </Typography>
                  <Stack spacing={1.25}>
                    {currentDetail.routes.map((route, index) => (
                      <Box key={index} borderLeft={2} borderColor="divider" pl={1.5}>
                        {route.description && <Typography variant="body2">{route.description}</Typography>}
                        <MetaLine
                          items={[
                            durationLabel(route.duration),
                            route.timeFrom && route.timeTo
                              ? `${route.timeFrom}–${route.timeTo}`
                              : (route.timeFrom ?? route.timeTo),
                          ]}
                        />
                        {route.points?.length ? <DetailList items={route.points} /> : null}
                      </Box>
                    ))}
                  </Stack>
                </Box>
              ) : null}

              {currentDetail?.modalities?.length ? (
                <Box mb={2}>
                  <Typography variant="subtitle2" gutterBottom>
                    {t('travelServices.tours.modalities')}
                  </Typography>
                  <Stack spacing={0.75}>
                    {currentDetail.modalities.map((modality) => (
                      <Box key={modality.id}>
                        {modality.name && <Typography variant="body2">{modality.name}</Typography>}
                        <MetaLine
                          items={[
                            durationLabel(modality.duration),
                            modality.languages?.map((language) => language.name).join(' · '),
                          ]}
                        />
                      </Box>
                    ))}
                  </Stack>
                </Box>
              ) : null}

              {(currentDetail?.location?.address || currentDetail?.location?.point) && (
                <Box mb={2}>
                  <Typography variant="subtitle2" gutterBottom>
                    {t('travelServices.tours.location')}
                  </Typography>
                  {currentDetail.location.point?.name && (
                    <Typography variant="body2">{currentDetail.location.point.name}</Typography>
                  )}
                  {[currentDetail.location.address, currentDetail.location.point?.address]
                    .filter(
                      (value, index, values): value is string => Boolean(value) && values.indexOf(value) === index,
                    )
                    .map((address) => (
                      <Typography key={address} variant="body2" color="text.secondary">
                        {address}
                      </Typography>
                    ))}
                  {currentDetail.location.point &&
                    !currentDetail.location.address &&
                    !currentDetail.location.point.address && (
                    <Typography variant="caption" color="text.secondary">
                      {currentDetail.location.point.lat}, {currentDetail.location.point.lng}
                    </Typography>
                  )}
                </Box>
              )}

              {(currentDetail?.meetingPoint?.description || currentDetail?.meetingPoint?.instructions?.length) && (
                <Box mb={2}>
                  <Typography variant="subtitle2" gutterBottom>
                    {t('travelServices.tours.meetingPoint')}
                  </Typography>
                  {currentDetail.meetingPoint.description && (
                    <Typography variant="body2" color="text.secondary" mb={0.5}>
                      {currentDetail.meetingPoint.description}
                    </Typography>
                  )}
                  {currentDetail.meetingPoint.instructions?.length ? (
                    <DetailList items={currentDetail.meetingPoint.instructions} />
                  ) : null}
                </Box>
              )}

              {(currentDetail?.cancellation?.text || currentDetail?.attributes?.availabilitySummary) && (
                <Box mb={2}>
                  <Typography variant="subtitle2" gutterBottom>
                    {t('travelServices.tours.conditions')}
                  </Typography>
                  {currentDetail.attributes?.availabilitySummary && (
                    <Typography variant="body2" color="text.secondary" mb={0.5}>
                      {currentDetail.attributes.availabilitySummary}
                    </Typography>
                  )}
                  {currentDetail.cancellation?.text && (
                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                      {currentDetail.cancellation.text}
                    </Typography>
                  )}
                </Box>
              )}

              {currentDetail?.remarks?.length ? (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    {t('travelServices.tours.remarks')}
                  </Typography>
                  <DetailList items={currentDetail.remarks.map((remark) => remark.text)} />
                </Box>
              ) : null}
            </Box>

            <Box minWidth={0}>
              <Box
                position={{ md: 'sticky' }}
                top={{ md: 0 }}
                border={1}
                borderColor="divider"
                borderRadius={1}
                p={1.5}
              >
                <Typography variant="subtitle2" gutterBottom>
                  {t('travelServices.tours.chooseExperience')}
                </Typography>
                {product.fromPricing ? (
                  <PriceBlock
                    pricing={product.fromPricing}
                    fromLabel={pricePresentation?.showFrom ? t('travelServices.tours.from') : undefined}
                    unit={pricePresentation?.unit ? t(`travelServices.tours.${pricePresentation.unit}`) : undefined}
                    align="left"
                  />
                ) : null}

                {offerOptions.isLoading && (
                  <Box display="flex" alignItems="center" gap={1} py={2}>
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="text.secondary">
                      {t('travelServices.tours.loadingOptions')}
                    </Typography>
                  </Box>
                )}
                {offerOptions.isError && (
                  <Alert
                    severity="warning"
                    sx={{ my: 1 }}
                    action={(
                      <Button
                        color="inherit"
                        size="small"
                        onClick={() => requestOptions(pickupId ? { pickupId } : {})}
                      >
                        {t('travelServices.tours.retryOptions')}
                      </Button>
                    )}
                  >
                    {t('travelServices.tours.optionsUnavailable')}
                  </Alert>
                )}

                {options?.pickups?.length ? (
                  <Box mt={1.5}>
                    <Typography variant="subtitle2" gutterBottom>
                      {t('travelServices.tours.pickup')}
                    </Typography>
                    <Box display="flex" gap={0.75} flexWrap="wrap">
                      {options.pickups.map((pickup) => (
                        <Chip
                          key={pickup.id}
                          clickable
                          color={pickup.id === pickupId ? 'primary' : 'default'}
                          variant={pickup.id === pickupId ? 'filled' : 'outlined'}
                          label={[pickup.name, pickup.type].filter(Boolean).join(' · ')}
                          onClick={() => {
                            if (pickup.id === pickupId && !offerOptions.isError) return;
                            setPickupId(pickup.id);
                            setSessionId('');
                            setTicketQuantities([]);
                            setOptions(tourOptionsAwaitingPickup);
                            requestOptions({ pickupId: pickup.id });
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                ) : null}

                {options?.sessions && (
                  <Box mt={1.5}>
                    <Typography variant="subtitle2" gutterBottom>
                      {t('travelServices.tours.session')}
                    </Typography>
                    {options.sessions.length === 0 ? (
                      <Alert severity="warning">{t('travelServices.tours.noSessions')}</Alert>
                    ) : (
                      <Stack spacing={0.75}>
                        {options.sessions.map((session) => (
                          <Button
                            key={session.id}
                            variant={session.id === sessionId ? 'contained' : 'outlined'}
                            color={session.availability === 0 ? 'inherit' : 'primary'}
                            disabled={session.availability === 0}
                            onClick={() => {
                              if (session.id === sessionId) return;
                              setSessionId(session.id);
                              setTicketQuantities([]);
                            }}
                            sx={{
                              alignItems: 'flex-start',
                              justifyContent: 'flex-start',
                              textAlign: 'left',
                              textTransform: 'none',
                            }}
                          >
                            <Box>
                              <Typography variant="body2" fontWeight={600}>
                                {tourSessionLabel(session) || t('travelServices.tours.session')}
                              </Typography>
                              {statusLabel(session, t).length > 0 && (
                                <Typography component="span" variant="caption" color="inherit" display="block">
                                  {statusLabel(session, t).join(' · ')}
                                </Typography>
                              )}
                            </Box>
                          </Button>
                        ))}
                      </Stack>
                    )}
                  </Box>
                )}

                {selectedSession && (
                  <Box mt={1.5}>
                    <TourTicketQuantities
                      session={selectedSession}
                      selection={ticketQuantities}
                      onChange={(id, delta) =>
                        setTicketQuantities((current) => changeTourTicketQuantity(current, id, delta))
                      }
                    />
                  </Box>
                )}

                {options?.message && (
                  <Typography variant="body2" color="text.secondary" mt={1.5}>
                    {options.message}
                  </Typography>
                )}

                {options?.refundPolicies && (
                  <Box mt={1.5}>
                    <Typography variant="subtitle2" gutterBottom>
                      {t('travelServices.tours.refundPolicies')}
                    </Typography>
                    {options.refundPolicies.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        {t('travelServices.tours.nonRefundable')}
                      </Typography>
                    ) : (
                      <Stack spacing={0.25}>
                        {options.refundPolicies.map((policy) => (
                          <Typography
                            key={policy.id}
                            variant="body2"
                            color="text.secondary"
                          >
                            {policy.period} ·{' '}
                            {policy.type === 'percentage'
                              ? `${policy.percentage}%`
                              : formatMoney(policy.amountPerItem)}
                          </Typography>
                        ))}
                      </Stack>
                    )}
                  </Box>
                )}

                <Button
                  variant="contained"
                  fullWidth
                  sx={{ mt: 1.5 }}
                  disabled={!canContinue}
                  onClick={() => onContinue(product.offer, selection)}
                >
                  {t('travelServices.tours.continueToQuote')}
                </Button>
                {!pickupValid && options?.pickups?.length ? (
                  <Typography color="warning.main" sx={{ fontSize: 11.5, mt: 0.75 }}>
                    {t('travelServices.tours.selectPickup')}
                  </Typography>
                ) : null}
                {!selectedSession && options?.sessions?.length ? (
                  <Typography color="warning.main" sx={{ fontSize: 11.5, mt: 0.75 }}>
                    {t('travelServices.tours.selectSession')}
                  </Typography>
                ) : null}
                {disabled && disabledReason && (
                  <Typography color="warning.main" sx={{ fontSize: 11.5, mt: 0.75 }}>
                    {disabledReason}
                  </Typography>
                )}
                <Typography color="text.secondary" sx={{ fontSize: 11.5, mt: 1 }}>
                  {t('travelServices.tours.quoteBeforeHold')}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
